import type {trpc} from "@/app/trpc";
import {startOfDay} from "@chayka/utils";
import {makeAutoObservable} from "mobx";
import {createContext} from "react";

export type TSuperType = (typeof trpc.booking.superTypes)["~types"]["output"][number];
export type TSubType = (typeof trpc.booking.subTypes)["~types"]["output"][number];
export type TRoom = (typeof trpc.booking.roomsBySubType)["~types"]["output"][number];
export type TEquipment = (typeof trpc.booking.equipment)["~types"]["output"][number];

export class BookingState {
  constructor() {
    makeAutoObservable(this);
  }

  superType: TSuperType | null = null;

  setSuperType = (superType: TSuperType | null) => {
    this.superType = superType;
  };

  subType: TSubType | null = null;

  setSubType = (subType: TSubType | null) => {
    this.subType = subType;
  };

  queryRoomId: number | null = null;

  setQueryRoomId = (queryRoomId: number | null) => {
    this.queryRoomId = queryRoomId;
  };

  date: Date = startOfDay(new Date());

  setDate = (date: Date) => {
    this.date = startOfDay(date);
  };

  room: TRoom | null = null;

  setRoom = (room: TRoom | null) => {
    this.room = room;
  };

  hours: number[] = [];

  setHours = (hours: number[]) => {
    this.hours = hours;
  };

  parkingSpot: TRoom | null = null;

  setParkingSpot = (parkingSpot: TRoom | null) => {
    this.parkingSpot = parkingSpot;
  };

  parkingHours: number[] = [];

  setParkingHours = (hours: number[]) => {
    this.parkingHours = hours;
  };

  carNumber: string = "";

  setCarNumber = (carNumber: string) => {
    this.carNumber = carNumber;
  };

  carModel: string = "";

  setCarModel = (carModel: string) => {
    this.carModel = carModel;
  };

  equipment: TEquipment[] = [];

  setEquipment = (equipment: TEquipment[]) => {
    this.equipment = equipment;
  };

  comment: string = "";

  setComment = (comment: string) => {
    this.comment = comment;
  };

  get isParkingBooking(): boolean {
    return this.subType?.code === "parking";
  }

  get bookingRoom(): TRoom | null {
    return this.isParkingBooking ? this.parkingSpot : this.room;
  }

  get bookingHours(): number[] {
    return this.isParkingBooking ? this.parkingHours : this.hours;
  }

  get fullRoomPrice(): number {
    return (this.bookingRoom?.price || 0) * this.bookingHours.length;
  }

  get fullParkingPrice(): number {
    if (this.isParkingBooking) {
      return 0;
    }

    return (this.parkingSpot?.price || 0) * this.parkingHours.length;
  }

  get fullEquipmentPrice(): number {
    return this.equipment.reduce((acc, eq) => acc + eq.price, 0);
  }

  get fullPrice(): number {
    return this.fullRoomPrice + this.fullParkingPrice + this.fullEquipmentPrice;
  }

  get hasDraft(): boolean {
    return Boolean(
      this.superType ||
        this.subType ||
        this.date.getTime() !== startOfDay(new Date()).getTime() ||
        this.room ||
        this.hours.length ||
        this.parkingSpot ||
        this.parkingHours.length ||
        this.carNumber.trim() ||
        this.carModel.trim() ||
        this.equipment.length ||
        this.comment.trim(),
    );
  }

  reset = () => {
    this.superType = null;
    this.subType = null;
    this.queryRoomId = null;
    this.date = startOfDay(new Date());
    this.room = null;
    this.hours = [];
    this.parkingSpot = null;
    this.parkingHours = [];
    this.carNumber = "";
    this.carModel = "";
    this.equipment = [];
    this.comment = "";
    this.hasToBeConfirmed = false;
  };

  hasToBeConfirmed = false;

  setHasToBeConfirmed = (hasToBeConfirmed: boolean) => {
    this.hasToBeConfirmed = hasToBeConfirmed;
  };
}

export const BookingStateContext = createContext(new BookingState());
